<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import { useArrowsAiAccount } from 'dashboard/composables/useArrowsAiAccount';
import { useUnsavedChangesGuard } from 'dashboard/composables/useUnsavedChangesGuard';
import {
  INITIAL_ARROWS_AI_TOOLS,
  localizeArrowsAiTools,
} from 'dashboard/composables/arrowsAi/initialTools';
import {
  displayArrowsAiToolMentions,
  hydrateArrowsAiToolMentions,
  serializeArrowsAiToolMentions,
} from 'dashboard/helper/arrowsAiToolMentions';
import { sanitizeArrowsAiInstructions } from 'dashboard/helper/arrowsAiInstructions';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import PageLayout from 'dashboard/components-next/arrowsAi/PageLayout.vue';
import PromptHistory from 'dashboard/components-next/arrowsAi/PromptHistory.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import UnsavedChangesDialog from 'dashboard/components-next/arrowsAi/UnsavedChangesDialog.vue';

const { t, te } = useI18n();
const route = useRoute();
const store = useStore();
const { instructionsLimit } = useArrowsAiAccount();
const { getAssistant, rememberAssistant, updateAssistant } =
  useArrowsAiAssistants();

const tools = computed(() =>
  localizeArrowsAiTools(INITIAL_ARROWS_AI_TOOLS, t, te)
);

onMounted(() => {
  store.commit('captainTools/SET_TOOLS', tools.value);
});

const assistant = computed(() => {
  const current = getAssistant(route.params.assistantId);
  if (current) rememberAssistant(current.id);
  return current;
});

const instructions = ref('');
const savedSnapshot = ref('');
const expanded = ref(false);
const isDirty = computed(() => instructions.value !== savedSnapshot.value);
const hydrate = value => hydrateArrowsAiToolMentions(value, tools.value);
const clean = value => sanitizeArrowsAiInstructions(hydrate(value));
const storedInstructions = computed(
  () =>
    sanitizeArrowsAiInstructions(
      serializeArrowsAiToolMentions(hydrate(instructions.value || ''))
    ) || ''
);
const overLimit = computed(
  () => storedInstructions.value.length > instructionsLimit.value
);
const editorMaxLength = computed(() =>
  Math.max(instructions.value.length, instructionsLimit.value)
);

watch(tools, next => {
  store.commit('captainTools/SET_TOOLS', next);
});

watch(
  assistant,
  (current, previous) => {
    if (!current) return;
    if (isDirty.value && previous?.id === current.id) return;
    const next = clean(current.instructions || '');
    instructions.value = next;
    savedSnapshot.value = next;
  },
  { immediate: true }
);

watch(instructions, value => {
  const next = hydrate(value);
  if (next !== value) instructions.value = next;
});

const persistInstructions = async () => {
  if (!assistant.value) {
    useAlert(t('ARROWS_AI.SETTINGS.ERROR'));
    throw new Error('missing-assistant');
  }

  const stored = storedInstructions.value;
  if (stored.length > instructionsLimit.value) {
    useAlert(
      t('ARROWS_AI.INSTRUCTIONS.TOO_LONG', { count: instructionsLimit.value })
    );
    throw new Error('instructions-too-long');
  }

  const next = clean(stored);
  instructions.value = next;
  try {
    await updateAssistant(assistant.value.id, { instructions: stored });
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SETTINGS.ERROR'));
    throw error;
  }
  savedSnapshot.value = next;
  useAlert(t('ARROWS_AI.INSTRUCTIONS.SUCCESS', { name: assistant.value.name }));
};

const handleSave = () => persistInstructions().catch(() => {});

const formatHistory = value =>
  sanitizeArrowsAiInstructions(
    displayArrowsAiToolMentions(hydrate(value || ''))
  );

const handleRestore = content => {
  instructions.value = clean(content);
};

const { dialogRef, confirmSave, discard, stay } = useUnsavedChangesGuard({
  isDirty,
  save: persistInstructions,
});
</script>

<template>
  <PageLayout :header-title="t('ARROWS_AI.INSTRUCTIONS.TITLE')">
    <MissingAssistant v-if="!assistant" />
    <div v-else class="flex flex-col max-w-[45rem] gap-6">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('ARROWS_AI.INSTRUCTIONS.DESCRIPTION') }}
      </p>
      <div
        class="[&_.editor-wrapper]:overflow-visible [&_.bottom-20]:!bottom-auto [&_.bottom-20]:!top-12"
        :class="
          expanded
            ? '[&_.ProseMirror-woot-style]:!max-h-[70vh] [&_.ProseMirror-woot-style]:!min-h-[28rem]'
            : ''
        "
      >
        <Editor
          v-model="instructions"
          :label="t('ARROWS_AI.INSTRUCTIONS.LABEL')"
          :placeholder="t('ARROWS_AI.INSTRUCTIONS.PLACEHOLDER')"
          :max-length="editorMaxLength"
          :message="
            overLimit
              ? t('ARROWS_AI.INSTRUCTIONS.TOO_LONG', {
                  count: instructionsLimit,
                })
              : ''
          "
          :message-type="overLimit ? 'error' : 'info'"
          :enable-canned-responses="false"
          enable-captain-tools
        />
      </div>
      <div class="flex items-center gap-2">
        <Button
          :label="t('ARROWS_AI.SAVE')"
          :disabled="overLimit"
          @click="handleSave"
        />
        <Button
          variant="faded"
          color="slate"
          :icon="expanded ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2'"
          :label="
            expanded
              ? t('ARROWS_AI.INSTRUCTIONS.COLLAPSE')
              : t('ARROWS_AI.INSTRUCTIONS.EXPAND')
          "
          @click="expanded = !expanded"
        />
      </div>
      <div class="flex flex-col gap-3">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('ARROWS_AI.HISTORY.TITLE') }}
        </h2>
        <PromptHistory
          :entries="assistant.promptHistory"
          :format-content="formatHistory"
          @restore="handleRestore"
        />
      </div>
    </div>
  </PageLayout>
  <UnsavedChangesDialog
    ref="dialogRef"
    @save="confirmSave"
    @discard="discard"
    @stay="stay"
  />
</template>
